import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Check } from 'lucide-react';

const PRODUCT_STATUSES = [
  { key: 'IN_USE', label: 'In Use', color: '#10b981', icon: '✓' },
  { key: 'PLANNED', label: 'Planned', color: '#3b82f6', icon: '◐' },
  { key: 'EVALUATING', label: 'Evaluating', color: '#f59e0b', icon: '○' },
  { key: 'NOT_APPLICABLE', label: 'N/A', color: '#6b7280', icon: '—' }
];

/**
 * CisaProductChip - Status chip for adopted products
 *
 * Props:
 *   productName - string
 *   status - 'IN_USE' | 'PLANNED' | 'EVALUATING' | 'NOT_APPLICABLE'
 *   onStatusChange - (newStatus) => void
 *   onRemove - () => void
 */
const CisaProductChip = ({ productName, status = 'IN_USE', onStatusChange, onRemove }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  const statusConfig = PRODUCT_STATUSES.find(s => s.key === status) || PRODUCT_STATUSES[0];

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [dropdownOpen]);

  // Close on Escape
  useEffect(() => {
    if (!dropdownOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dropdownOpen]);

  const handleStatusSelect = (newStatus) => {
    onStatusChange?.(newStatus);
    setDropdownOpen(false);
  };

  return (
    <div
      className={`cisa-product-chip cisa-product-chip--${status.toLowerCase().replace('_', '-')}`}
      style={{ '--chip-color': statusConfig.color }}
      ref={containerRef}
    >
      <span className="cisa-product-chip-icon">{statusConfig.icon}</span>
      <span className="cisa-product-chip-name">{productName}</span>

      {/* Status dropdown trigger */}
      <button
        type="button"
        className="cisa-product-chip-status"
        onClick={() => setDropdownOpen(o => !o)}
        title={`Status: ${statusConfig.label}. Click to change.`}
      >
        <span className="cisa-product-chip-status-label">{statusConfig.label}</span>
        <ChevronDown size={12} className={dropdownOpen ? 'cisa-chip-chevron-rotated' : ''} />
      </button>

      {/* Status dropdown */}
      {dropdownOpen && (
        <div className="cisa-product-chip-dropdown">
          {PRODUCT_STATUSES.map(s => (
            <button
              key={s.key}
              type="button"
              className={`cisa-product-chip-option ${s.key === status ? 'cisa-product-chip-option--selected' : ''}`}
              onClick={() => handleStatusSelect(s.key)}
            >
              <span className="cisa-product-chip-option-icon" style={{ color: s.color }}>
                {s.icon}
              </span>
              <span className="cisa-product-chip-option-label">{s.label}</span>
              {s.key === status && <Check size={12} className="cisa-product-chip-check" />}
            </button>
          ))}
        </div>
      )}

      {/* Remove button */}
      <button
        type="button"
        className="cisa-product-chip-remove"
        onClick={onRemove}
        title={`Remove ${productName}`}
      >
        <X size={12} />
      </button>
    </div>
  );
};

export { PRODUCT_STATUSES };
export default CisaProductChip;
